/**
 * <expandable-group> — generic vertical disclosure/accordion (sections/expandable.liquid,
 * sections/expandable-media.liquid).
 * CSS owns all motion (grid-template-rows transition keyed on [data-expanded]);
 * this element only keeps data-expanded, aria-expanded and panel.inert in sync,
 * and announces every real change with a bubbling `expandable:toggle` event on
 * the item (detail: { expanded }).
 * The initial state is server-rendered by Liquid — read on init, never rewritten,
 * so the default-open item is open on first paint.
 *
 * Deliberately no close-on-outside-click and no close-on-Escape (unlike
 * schedule-accordion): on a vertical content accordion a user scrolling and
 * tapping a product would lose their panel. One scroll side-effect only:
 * in exclusive mode, when opening an item closes an open item located above
 * it, the clicked item is scrolled back into view once the closing panel has
 * collapsed — cancelled if the user scrolls themselves during the wait.
 *
 * Optional host attributes, each a media query — absent means today's behaviour:
 * - data-keep-one-open: while it matches, the only open item cannot be closed,
 *   and the first item opens on init / on query change if none is open
 * - data-auto-scroll: the compensating scroll only runs while it matches
 *
 * Everything is block-scoped inside the customElements guard (colorway-morph.ts
 * pattern): components compile as classic scripts sharing one global scope, so a
 * second <script> for this file on the same page must not redeclare anything.
 */
if (!customElements.get('expandable-group')) {
    const REF = {
        item: '[data-ref="expandable-item"]',
        trigger: '[data-ref="expandable-trigger"]',
        panel: '[data-ref="expandable-panel"]',
    };
    class ExpandableGroup extends HTMLElement {
        allowMultiple = false;
        /** data-keep-one-open media query — null when the attribute is absent. */
        keepOneOpen = null;
        /** data-auto-scroll media query — null when the attribute is absent (scroll always allowed). */
        autoScroll = null;
        /** Cleanup for a compensating scroll still waiting on transitionend — null when idle. */
        cancelPendingScroll = null;
        connectedCallback() {
            this.allowMultiple = this.dataset.allowMultiple === 'true';
            this.keepOneOpen = this.dataset.keepOneOpen ? window.matchMedia(this.dataset.keepOneOpen) : null;
            this.autoScroll = this.dataset.autoScroll ? window.matchMedia(this.dataset.autoScroll) : null;
            // Delegation on the group survives blocks being added, removed or
            // reordered in the Theme Editor without re-init
            this.addEventListener('click', this.handleClick);
            this.keepOneOpen?.addEventListener('change', this.ensureOneOpen);
            this.normalise();
            this.ensureOneOpen();
        }
        disconnectedCallback() {
            this.removeEventListener('click', this.handleClick);
            this.keepOneOpen?.removeEventListener('change', this.ensureOneOpen);
            this.keepOneOpen = null;
            this.autoScroll = null;
            this.cancelPendingScroll?.();
        }
        /** Items directly owned by this group (not by a nested one). */
        get items() {
            return Array.from(this.querySelectorAll(REF.item)).filter((item) => item.closest('expandable-group') === this);
        }
        /** Reconcile a server-rendered state that exclusive mode cannot honour. */
        normalise() {
            if (this.allowMultiple)
                return;
            const open = this.items.filter((item) => item.dataset.expanded === 'true');
            open.slice(1).forEach((item) => this.setExpanded(item, false));
        }
        /** data-keep-one-open: while the query matches, open the first item if none is open. */
        ensureOneOpen = () => {
            if (!this.keepOneOpen?.matches)
                return;
            const items = this.items;
            if (items.length === 0 || items.some((item) => item.dataset.expanded === 'true'))
                return;
            this.setExpanded(items[0], true);
        };
        handleClick = (event) => {
            const trigger = event.target.closest(REF.trigger);
            if (!trigger)
                return;
            const item = trigger.closest(REF.item);
            const items = this.items;
            if (!item || !items.includes(item))
                return;
            const willExpand = item.dataset.expanded !== 'true';
            // data-keep-one-open: closing the only open item is a no-op while the query matches
            if (!willExpand && this.keepOneOpen?.matches) {
                const othersOpen = items.some((other) => other !== item && other.dataset.expanded === 'true');
                if (!othersOpen)
                    return;
            }
            let closingAbove;
            if (willExpand && !this.allowMultiple) {
                // Exclusive mode holds at most one item open (normalise + this handler),
                // so at most one panel is closing — no multi-panel handling needed
                closingAbove = items.slice(0, items.indexOf(item)).find((other) => other.dataset.expanded === 'true');
                items.filter((other) => other !== item).forEach((other) => this.setExpanded(other, false));
            }
            this.setExpanded(item, willExpand);
            // data-auto-scroll absent → always allowed (expandable section); present → only while it matches
            if (closingAbove && (this.autoScroll?.matches ?? true))
                this.scrollToItem(item, closingAbove);
        };
        /**
         * Closing an open item above the clicked one shifts the page content up by
         * its panel height — once that panel has collapsed, bring the clicked item
         * back to the top of the viewport. The header offset is handled in CSS via
         * scroll-margin-block-start on the item, not computed here.
         */
        scrollToItem(item, closingItem) {
            this.cancelPendingScroll?.();
            // Reduced motion: transitions are disabled, geometry is already final
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                item.scrollIntoView({ block: 'start', behavior: 'auto' });
                return;
            }
            const panel = closingItem.querySelector(REF.panel);
            const scroll = () => {
                cleanup();
                item.scrollIntoView({ block: 'start', behavior: 'smooth' });
            };
            const onTransitionEnd = (event) => {
                // The panel may fire transitionend for other properties — wait for height
                if (event.propertyName !== 'grid-template-rows')
                    return;
                scroll();
            };
            // The user scrolling during the wait means they took over — do not fight them
            const cancel = () => cleanup();
            // Safety net in case transitionend never fires (panel removed, transition interrupted)
            const timer = window.setTimeout(scroll, 600);
            const cleanup = () => {
                window.clearTimeout(timer);
                panel?.removeEventListener('transitionend', onTransitionEnd);
                window.removeEventListener('wheel', cancel);
                window.removeEventListener('touchmove', cancel);
                this.cancelPendingScroll = null;
            };
            panel?.addEventListener('transitionend', onTransitionEnd);
            window.addEventListener('wheel', cancel, { passive: true });
            window.addEventListener('touchmove', cancel, { passive: true });
            this.cancelPendingScroll = cleanup;
        }
        setExpanded(item, expanded) {
            const trigger = item.querySelector(REF.trigger);
            const panel = item.querySelector(REF.panel);
            const changed = (item.dataset.expanded === 'true') !== expanded;
            item.dataset.expanded = String(expanded);
            trigger?.setAttribute('aria-expanded', String(expanded));
            // inert keeps links/buttons inside a closed panel out of the tab order
            if (panel)
                panel.inert = !expanded;
            // Exclusive mode re-closes already-closed items — only real changes are announced
            if (changed) {
                item.dispatchEvent(new CustomEvent('expandable:toggle', { bubbles: true, detail: { expanded } }));
            }
        }
    }
    customElements.define('expandable-group', ExpandableGroup);
    // Theme Editor: opening an item's settings opens the item. Routed through
    // .click() so exclusivity and inert follow the exact same path as a user
    // interaction — no second implementation to keep in sync.
    document.addEventListener('shopify:block:select', (event) => {
        const target = event.target;
        const item = target?.closest(REF.item);
        if (!item || !item.closest('expandable-group'))
            return;
        if (item.dataset.expanded === 'true')
            return;
        item.querySelector(REF.trigger)?.click();
    });
}
